import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Select, MenuItem } from '@material-ui/core';
import Table from '../../../FlowConfigurationInputs/Table/Table';
import { TextInput } from '../../../FlowConfigurationInputs/TextInput/NewTextInput';
import { CredentialPicker } from '../../../FlowConfigurationInputs/CredentialPicker/CredentialPicker';
import VariablePicker from '../../../FlowConfigurationInputs/VariablePicker/VariablePicker';
import { isActionValueController } from "../../../../helpers/helper";
import AutomService from "../../../../services/AutomService/AutomService";
import FieldLabel from '../../../FlowConfigurationInputs/FieldLabel/FieldLabel';
// Import Recoil
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import { saveAvailable, initialActions, changedFields } from "../../../../recoil-store/recoil-state";

// Import Helpers
import { CheckIfFlowUpdated } from '../../../../helpers/helper';

// Import Components
import AppCredential from "../AppCredential/AppCredential";

function Patch({ action, variables, setOpenVariable, automId, showAlert, automVersionId }) {
    const { t } = useTranslation();
    const initialActionsData = useRecoilValue(initialActions);
    const setSaveAvailable = useSetRecoilState(saveAvailable);
    const [changedFieldsState, setChangeFields] = useRecoilState(changedFields);
    const label = 'CONTENT.CONFIGURATION-FORM.RESTFUL-WEB-SERVICE.';
    const [credVerify, setCredVerify] = useState('none');
    const [content, setContent] = useState('urlencoded');
    const headers = [label + 'NAME', label + 'VALUE'];
    const options = {
        tableHeader: { headers, title: label + 'HEADER-FIELD' },
        tableBody: { headers, title: label + 'BODY-PARAMS' }
    };

    useEffect(() => {
        if (!action.fields.authentication) {
            action.fields.authentication = { value: 'none', isRequired: false };
        } else {
            setCredVerify(action.fields.authentication.value);
        }
        if (!action.fields.contentType) {
            action.fields.contentType = { value: 'urlencoded', isRequired: true };
        } else {
            setContent(action.fields.contentType.value);
        }
    }, [action]);

    const handleCredSelect = (event) => {
        let value = event.target.value;
        if (value !== 'basic') {
            delete action.fields.loginUser;
            delete action.fields.loginPassword;
        }
        action.fields.authentication.value = value;
        setCredVerify(value);

        const field = initialActionsData[action.id]?.fields.authentication;
        const oldValue = !field ? undefined : field.value;
        CheckIfFlowUpdated('authentication', action, oldValue, value, setSaveAvailable, setChangeFields, changedFieldsState);
    }

    const handleContentSelect = (event) => {
        let value = event.target.value;
        const field = initialActionsData[action.id]?.fields.contentType;
        const oldValue = !field ? undefined : field.value;
        CheckIfFlowUpdated('contentType', action, oldValue, value, setSaveAvailable, setChangeFields, changedFieldsState);
        if (value === 'urlencoded' || value === 'form-data') {
            action.fields.bodyParams = { type: 'TABLE', value: [] };
        } else {
            action.fields.bodyParams = { value: '' };
        }
        action.fields.contentType.value = value;
        setContent(value);
    }

    const handleClick = async () => {
        let res = isActionValueController(action.fields);
        if (res.indexOf(false) === -1) {
            action.automId = automId;
            action.automVersionId = automVersionId;
            let response = await AutomService.runAction(action);
            if (response) {
                showAlert(t('FLOW-MESSAGES.LIBRARY.' + response.message), response.status);
            }
        } else {
            showAlert(t('ACTIVE-DIRECTORY.NOT-NULL'), false);
        }
    }

    return (
        <div>
            <div className="conf-field-container">
                <FieldLabel name={t(label + 'REQUEST-URI')} is_required />
                <TextInput action={action} inputName="uriAddress" variables={variables}
                    setOpenVariable={setOpenVariable}
                    isRequired={true} fieldType="string" displayName={t(label + 'REQUEST-URI')} hideRequired />
            </div>
            <div className="conf-field-container">
                <FieldLabel name={t(label + 'AUTHENTICATION')} />
                <Select value={credVerify} onChange={handleCredSelect} variant="outlined" fullWidth
                    style={{ width: "90%" }}>
                    <MenuItem value="none">{t(label + 'NO-AUTH')}</MenuItem>
                    <MenuItem value="basic">{t(label + 'BASIC-AUTH')}</MenuItem>
                    <MenuItem value="oauth20">{t(label + 'OAUTH20-AUTH')}</MenuItem>
                </Select>
            </div>
            {credVerify === 'basic' &&
                <div>
                    <div className="conf-field-container">
                        <FieldLabel name={t(label + 'USERNAME')} is_required />
                        <CredentialPicker action={action} inputName="loginUser" variables={variables}
                            setOpenVariable={setOpenVariable} isRequired={true} hideRequired />
                    </div>
                    <div className="conf-field-container">
                        <FieldLabel name={t(label + 'PASSWORD')} is_required />
                        <CredentialPicker action={action} inputName="loginPassword" variables={variables}
                            setOpenVariable={setOpenVariable} isPassword={true} isRequired={true}
                            hideRequired />
                    </div>
                </div>
            }
            {credVerify === 'oauth20' &&
                <div>
                    <AppCredential action={action} />
                </div>
            }
            <div className="form-element">
                <Table automVersionId={automVersionId} action={action} inputName="headerParams"
                    options={options.tableHeader}
                    setOpenVariable={setOpenVariable} isRequired={false} headerWithVariable={true} />
            </div>
            <div className="conf-field-container">
                <FieldLabel name="Content Type" />
                <Select value={content} onChange={handleContentSelect} variant="outlined" fullWidth
                    style={{ width: "90%" }}>
                    <MenuItem value="none">none</MenuItem>
                    <MenuItem value="urlencoded">urlencoded</MenuItem>
                    <MenuItem value="form-data">form-data</MenuItem>
                    <MenuItem value="application/json">application/json</MenuItem>
                    <MenuItem value="application/xml">application/xml</MenuItem>
                    <MenuItem value="text/plain">text/plain</MenuItem>
                    <MenuItem value="text/xml">text/xml</MenuItem>
                    <MenuItem value="text/html">text/html</MenuItem>
                </Select>
            </div>
            {content !== 'none' &&
                <div className="form-element">
                    {content === 'urlencoded' || content === 'form-data' ?
                        <Table automVersionId={automVersionId} action={action} inputName="bodyParams"
                            options={options.tableBody}
                            setOpenVariable={setOpenVariable} isRequired={false} formData={content === 'form-data'}
                            automId={automId} headerWithVariable={true} />
                        :
                        <TextInput action={action} inputName="bodyParams" variables={variables}
                            setOpenVariable={setOpenVariable} multiline rows={4}
                            fieldType={content === 'application/json' ? 'object' : 'string'}
                            displayName={t(label + 'BODY-PARAMS')} />
                    }
                </div>
            }
            <div className="conf-field-container">
                <FieldLabel name={t('CONTENT.CONFIGURATION-FORM.RESTFUL-WEB-SERVICE.ASSIGN-RESPONSE-BODY')}
                />
                <VariablePicker action={action} inputName="set" variables={variables} setOpenVariable={setOpenVariable}
                    remove={['data table', 'array']} />
                <small className="conf-field-desc"><em>{t('CONTENT.CONFIGURATION-FORM.PREFERRED-TYPE')}</em></small>
            </div>
            <div className="conf-field-container">
                <FieldLabel name={t('CONTENT.CONFIGURATION-FORM.RESTFUL-WEB-SERVICE.ASSIGN-RESPONSE-HEADER')} />
                <VariablePicker action={action} inputName="setHeader" variables={variables}
                    setOpenVariable={setOpenVariable}
                    filter="object" />
                <small className="conf-field-desc"><em>{t('CONTENT.CONFIGURATION-FORM.FIELD-MUST-BE-OBJECT-TYPE')}</em></small>
            </div>
            <button className="form-button" onClick={() => handleClick()}>Run Once</button>
        </div>
    )
}

export default Patch;
